/* eslint-disable no-param-reassign */
import React, { useEffect, useMemo, useState } from 'react';

import {
  HStack, Stack, Text, StackProps, useToast, Flex,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { camelize } from 'humps';
import { isEmpty } from 'lodash';
import {
  Controller, FormProvider, useFieldArray, useForm, UseFormReturn,
} from 'react-hook-form';
import { useMutation } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import { Button, Icon, Select } from 'components';
import Label from 'components/inputs/label';
import {
  forms, FormType, inputs, InputType,
} from 'constants/FormBuilder';
import DeleteModal from 'containers/Table/Delete';
import { ContentRoutes } from 'enums/routes';
import { GeneralStatus } from 'enums/status';
import useActions from 'hooks/useActions';
import useTenant from 'stores/useTenant';
import useUser from 'stores/useUser';
import { compareByOperator } from 'utils/comparisons';
import { debounceCompany } from 'utils/debounces';
import masks from 'utils/masks';
import { REQUIRED_MESSAGE } from 'validations/general';

interface InputBuilderProps extends UseFormReturn {
  input: InputType
}

function InputBuilder({
  input, control, setValue, watch, getValues, setError, clearErrors,
}: InputBuilderProps) {
  const hideManager = watch(input.hidden?.[0]);
  const [forced, forceRefresh] = useState(false);
  const { tenant } = useTenant();
  const hidden = useMemo(
    () => compareByOperator(hideManager, input.hidden?.[1], input.hidden?.[2]),
    [hideManager]);

  const Field = useMemo(() => inputs[input?.type], [input]);

  if (hidden) {
    return null;
  }

  if (input.type === 'array') {
    const { append, fields, remove } = useFieldArray({
      name: input.name,
      control,
      keyName: 'key',
    });
    useEffect(() => {
      if (input.isMultiple && fields.length < 1) {
        append({});
      }
    }, []);

    return (
      <Stack spacing={4}>
        {fields.map((value, index) => (
          <HStack>
            {input.fields.map(({
              required,
              type,
              mask,
              onChange,
              props,
              rules,
              name,
            }) => {
              const ArrayField = inputs[type];
              const fieldMask = (e) => {
                if (input.mask) {
                  e.target.value = masks[mask].resolve(e.target?.value);
                }
                return e;
              };
              return (
                <Controller
                  control={control}
                  rules={{
                    required: required ? REQUIRED_MESSAGE : false,
                    onChange(event: any): void {
                      if (isEmpty(event.target.value)) {
                        setValue(event.target.name, null);
                      }
                    },
                    ...rules,
                  }}
                  name={input.name.concat(`.${index}.${name}`)}
                  defaultValue={value[name]}
                  render={({ field }) => (
                    <ArrayField
                      {...field}
                      watch={tenant}
                      {...props}
                      onChange={(event) => {
                        // eslint-disable-next-line no-param-reassign
                        event = fieldMask(event);
                        field.onChange(event);
                        if (onChange) {
                          onChange(event, setValue, getValues, setError, clearErrors);
                        }
                      }}
                    />
                  )}
                />
              );
            })}
          </HStack>
        ))}
        <HStack justifyContent="space-between">
          <Button
            p={0}
            alignSelf="flex-start"
            variant="ghost.blue"
            onClick={() => append({})}>
            <Icon name="plus" />
            <Text
              variant="mediumBold"
              ml={2}>
              Adicionar Item
            </Text>
          </Button>
          <Button
            p={0}
            alignSelf="flex-start"
            variant="ghost.blue"
            onClick={() => {
              setValue(input.name.concat('.{fields.length - 1}'), undefined);
              remove(fields.length - 1);
            }}>
            <Icon
              color="system.error.default"
              name="minus"
            />
            <Text
              color="system.error.default"
              variant="mediumBold"
              ml={2}>
              Remover Item
            </Text>
          </Button>
        </HStack>
      </Stack>
    );
  }

  const mask = (e) => {
    if (input.mask) {
      e.target.value = masks[input.mask].resolve(e.target?.value);
    }
    return e;
  };
  if (input.isMultiple) {
    const fields = watch(input.name)?.filter((field) => !!field);
    const emptyField = input.type === 'image' ? { empty: true } : {};
    useEffect(() => {
      if (isEmpty(fields)) {
        setValue(input.name.concat('.0'), emptyField);
        forceRefresh(!forced);
      }
    }, [fields]);

    return (
      <Stack spacing={4}>
        {input.type === 'image' && (
        <Label
          isDisabled={false}
          label={input.props.label}
        />
        )}
        {fields?.map((value, index) => (
          <HStack>
            <Controller
              control={control}
              rules={{
                required: input.required ? REQUIRED_MESSAGE : false,
                ...input.rules,
              }}
              defaultValue={value}
              name={input.name.concat(`.${index}`)}
              render={({ field }) => (
                <Field
                  {...field}
                  watch={tenant}
                  {...input.props}
                  value={value}
                  onChange={(event) => {
                    // eslint-disable-next-line no-param-reassign
                    event = mask(event);
                    field.onChange(event);
                    if (input.onChange) {
                      input.onChange(event, setValue, getValues, setError, clearErrors);
                    }
                  }}
                />
              )}
            />
            <Icon
              hidden={index + 1 !== fields.length}
              cursor="pointer"
              onClick={() => {
                setValue(input.name.concat(`.${index}`), undefined);
              }}
              position="relative"
              top={input.type === 'image' ? 0 : 2}
              color="gray.500"
              name="trash"
            />
          </HStack>
        ))}
        <Button
          p={0}
          alignSelf="flex-start"
          variant="ghost.blue"
          onClick={() => setValue(input.name.concat(`.${fields.length}`), emptyField)}>
          <Icon name="plus" />
          <Text
            variant="mediumBold"
            ml={2}>
            Adicionar Item
          </Text>
        </Button>
      </Stack>
    );
  }
  return (
    <Controller
      control={control}
      rules={{
        required: input.required ? REQUIRED_MESSAGE : false,
        ...input.rules,
      }}
      name={input.name}
      render={({ field }) => (
        <Field
          {...field}
          watch={tenant}
          {...input.props}
          onChange={(event) => {
            // eslint-disable-next-line no-param-reassign
            event = mask(event);
            field.onChange(event);
            if (input.type === 'date' && event.target.value.length >= 10) {
              const format = event.target.value.length === 10 ? 'DD/MM/YYYY' : undefined;
              const date = dayjs(event.target.value, format);
              if (date.isValid()) {
                setValue(input.name, date);
              }
            }
            if (input.onChange) {
              input.onChange(event, setValue, getValues, setError, clearErrors);
            }
          }}
        />
      )}
    />
  );
}

interface SectionBuilderProps extends StackProps {
  form: UseFormReturn
  fields: FormType['fields']
}

function SectionBuilder({ fields = [], form, ...rest }:SectionBuilderProps) {
  return (
    <Stack
      spacing={4}
      {...rest}>
      {fields.map((section, index) => {
        const Wrapper = section?.length > 1 ? HStack : React.Fragment;

        return (
          <React.Fragment key={`form-section-${index}-${section.length}`}>
            <Wrapper>
              {section.length === 0 && (
              <Flex
                bg="gray.50"
                width="100%"
                height="px"
              />
              )}
              {section?.map((input) => (
                <InputBuilder
                  key={`form-${index}-input-${input.name}`}
                  input={input}
                  {...form}
                />
              ))}
            </Wrapper>
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

interface FormBuilderProps {
  onClose: () => void
  content: string
  defaultValues?: any
  onSuccess?: () => void
}

export default function FormBuilder({
  content, onClose, defaultValues, onSuccess,
}: FormBuilderProps) {
  const { user } = useUser();
  const { setTenant } = useTenant();
  const { onDelete, onRestore } = useActions();
  const { push, goBack } = useHistory();
  const { id } = useParams<GeneralRoute>();
  const toast = useToast();
  const build = useMemo(() => forms[content], [content]);
  const isntSuperUser = useMemo(
    () => !user?.isSuperUser || content === ContentRoutes.SuperUsers,
    [content, user],
  );

  useEffect(() => () => setTenant(null), []);

  const form = useForm({
    defaultValues,
  });
  const { handleSubmit, setError } = form;

  const { mutate } = useMutation((data) => {
    if (defaultValues?.id) {
      return build.updateFn({ id: defaultValues.id, data });
    }
    return build.createFn({ data, parentId: id });
  }, {
    onSuccess: () => {
      setTenant(null);
      toast({
        title: 'Salvo com sucesso!',
        description: build.successText,
        status: 'success',
        duration: 5000,
        position: 'top-right',
        isClosable: true,
      });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      if (error.errors) {
        error.errors.forEach((err) => {
          setError(camelize(err.field), err);
        });
      } else {
        toast({
          title: 'Ocorreu um erro.',
          description: error.message,
          status: 'error',
          duration: 5000,
          position: 'top-right',
          isClosable: true,
        });
      }
    },
  });
  const onSubmit = (data) => {
    mutate(data);
  };

  return (
    <FormProvider {...form}>
      <DeleteModal
        onSuccess={content && content !== ContentRoutes.VehicleAccessories ? () => { push(`/table/${content}`); } : goBack}
        fallbackContent={ContentRoutes.Users}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          spacing={6}
          p={6}>
          <Stack
            borderBottom="1px"
            borderColor="gray.150"
            pb={5}
            hidden={isntSuperUser || user?.id === defaultValues?.id}>
            <Controller
              control={form.control}
              name="company"
              rules={{
                validate: (data) => {
                  if (
                    !data
                    && !isntSuperUser
                    && ![ContentRoutes.Companies, ContentRoutes.Company].includes(content as any)
                    && user.id !== defaultValues.id
                  ) {
                    return 'O campo é obrigatório';
                  }

                  return null;
                },
              }}
              render={({ field }) => (
                <Select
                  label="Empresa"
                  loadOptions={debounceCompany}
                  {...field}
                  onChange={(value) => {
                    field.onChange(value);
                    setTenant(value as any);
                  }}
                />
              )}
            />
          </Stack>
          <SectionBuilder
            fields={build.fields}
            form={form}
          />
          <HStack
            w="100%"
            justifyContent="space-between">
            {defaultValues?.id
              ? (
                <Text
                  variant="xSmallRegular"
                  onClick={() => {
                    if (defaultValues.status === GeneralStatus.Inactive) {
                      onRestore(`/${content}/${defaultValues.id}`).then(() => {
                        onSuccess();
                        onClose();
                      });
                    } else {
                      onDelete(`/${content}/${defaultValues.id}`, defaultValues);
                    }
                  }}
                  cursor="pointer"
                  color="system.error.default">
                  {defaultValues.status === GeneralStatus.Inactive
                    ? build.enableText
                    : build.disableText}
                </Text>
              ) : <span />}
            <HStack>
              <Button
                onClick={onClose}
                size="xs"
                variant="secondary.teal">
                <Text variant="smallBold">
                  {build.cancelText}
                </Text>
              </Button>
              <Button
                type="submit"
                size="xs"
                variant="primary.blue">
                <Text variant="smallBold">
                  {build.submitText}
                </Text>
              </Button>
            </HStack>
          </HStack>
        </Stack>
      </form>
    </FormProvider>
  );
}
